import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, TypeTag } from '../../components/ui';
import FirstRunSteps from './FirstRunSteps';
import DescribeBox from './DescribeBox';
import StarterList from './StarterList';
import { useHomeData } from './useHomeData';
import { fieldForFunction } from './FirstRunWork';
import { roleShort, STARTER_POLL_INTERVAL_MS, STARTER_POLL_TIMEOUT_MS } from './homeData';

const STRIP = 'Describe a task → Review the plan → Copy to canvas → Save any run of steps as your own task';

const MINI_NODES = [
  { label: 'Upload notes', type: 'INPUT' },
  { label: 'Extract themes', type: 'PROMPT' },
  { label: 'Pull quotes', type: 'PROMPT' },
  { label: 'Write summary', type: 'PROMPT' },
];

export default function FirstRunWays() {
  const navigate = useNavigate();
  const { profile, starters, refreshStarters } = useHomeData();
  const [text, setText] = useState('');
  const [timedOut, setTimedOut] = useState(false);

  const role = profile && profile.role ? profile.role : '';
  const field = profile && profile.group_function ? profile.group_function : '';
  const short = roleShort(role);
  const hasStarters = (starters || []).length > 0;
  const polling = Boolean(role && field) && !hasStarters && !timedOut;

  useEffect(() => {
    if (!polling) return undefined;
    const interval = setInterval(() => refreshStarters(), STARTER_POLL_INTERVAL_MS);
    const timeout = setTimeout(() => {
      clearInterval(interval);
      setTimedOut(true);
    }, STARTER_POLL_TIMEOUT_MS);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [polling, refreshStarters]);

  const placeholder = hasStarters ? `${starters[0].title}…` : 'Describe what you need done';
  const article = /^[aeiou]/i.test(short) ? 'an' : 'a';

  return (
    <div className="min-h-screen bg-paper text-ink">
      <div className="max-w-5xl mx-auto px-6 py-8 flex flex-col gap-8">
        <FirstRunSteps current={2} />
        <h1 className="font-display text-4xl font-bold">Two ways to get work done.</h1>
        <div className="grid gap-6 md:grid-cols-2">
          <Card variant="static" className="p-6 flex flex-col gap-4">
            <h2 className="font-display text-xl font-semibold">Describe a task</h2>
            <DescribeBox
              value={text}
              onChange={setText}
              placeholder={placeholder}
              label="Tell us what you need done"
            />
            {field && (
              <p className="font-semibold text-ink m-0">
                Tasks people in {fieldForFunction(field).toLowerCase()} run
              </p>
            )}
            {short && (
              <p className="text-sm text-pencil m-0">
                for {article} {short} ·{' '}
                <Link to="/start" className="underline focus-ring">change</Link>
              </p>
            )}
            {timedOut && !hasStarters ? (
              <p className="text-pencil m-0">No starter tasks for {short} yet.</p>
            ) : (
              <StarterList starters={starters} limit={6} onPick={(s) => setText(s.title)} />
            )}
            {polling && <p className="text-sm text-pencil m-0">Picking tasks for {short}…</p>}
          </Card>
          <Card variant="static" className="p-6 flex flex-col gap-4">
            <h2 className="font-display text-xl font-semibold">Start on a blank canvas</h2>
            <div className="flex flex-col gap-2" aria-hidden="true">
              {MINI_NODES.map((n, i) => (
                <div
                  key={n.label}
                  style={{ marginLeft: `${(i % 2) * 24}px` }}
                  className="self-start flex items-center gap-2 bg-card border-1.5 border-ink rounded-[10px] px-3 py-1.5 text-sm"
                >
                  <TypeTag type={n.type} />
                  <span>{n.label}</span>
                </div>
              ))}
            </div>
            <div>
              <Button variant="primary" onClick={() => navigate('/canvas?new=1')}>
                Open a blank canvas
              </Button>
            </div>
          </Card>
        </div>
        <p className="text-sm text-pencil m-0">{STRIP}</p>
      </div>
    </div>
  );
}
